import React, { useState, useRef } from 'react';

import { Chip, Typography, Menu, Popover, ButtonGroup, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Button, MenuItem, Translate } from '@hydra/common/components';
import DownloadPdfDialog from '@hydra/common/components/DownloadPdfDialog';
import { useUserContext, useReviewContext } from '@hydra/common/context';

import { usePdfDownload, useI18n } from '@hydra/common/customHooks';
import { sendEvent } from '@hydra/utils/gtm';
import { getIsDudaUser } from '@hydra/utils/userUtil';
import settings from '@hydra/utils/settings';

const ITEM_HEIGHT = 54;
const MAX_ITEMS = 5.5;

const useStyles = makeStyles(theme => ({
  nameContainer: {
    maxWidth: 90,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontSize: theme.typography.fontSizeSmall,
    fontWeight: theme.typography.fontWeightRegular
  },
  editIcon: {
    marginLeft: 'auto'
  },
  newTemplateContainer: {
    justifyContent: 'center'
  },
  defaultTemplateChip: {
    marginTop: theme.spacing(0.25),
    maxWidth: 70,
    marginLeft: theme.spacing(1),
    padding: 0,
    fontSize: theme.typography.fontSizeTiny,
    height: 18
  },
  btnCtaContainer: {
    display: 'flex',
    justifyContent: 'center',
    margin: theme.spacing(2, 0)
  },
  upgrade: {
    margin: theme.spacing(2, 2, 0)
  }
}));

const DownloadPdfBtn = ({ url, pdfTemplates, className: classNameProp }) => {
  const { user } = useUserContext();
  const isDudaUser = getIsDudaUser(user);

  const { locale } = useI18n();
  const classes = useStyles();
  const { busy, downloadPdf, error, errorMessage } = usePdfDownload({ url });
  const pdfBtn = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const { isFree, isAssistant } = useReviewContext();
  const enablePdfDowload = !isFree && !isAssistant;

  const openMenu = () => setShowMenu(true);
  const closeMenu = () => setShowMenu(false);

  const handleDownloadPdf = ({ id, defaultTemplate }) => {
    sendEvent(`review-actions-downloadpdf-${defaultTemplate ? 'defaulttemplate' : 'template'}-link`);
    closeMenu();
    downloadPdf({ templateId: id });
  };

  const handleEditTemplate = (event, id, defaultTemplate) => {
    event.stopPropagation();
    sendEvent(`review-actions-editpdf-${defaultTemplate ? 'defaulttemplate' : 'template'}-link`);
    closeMenu();
    window.location.href = `/${locale}/pdf-editor/${id}`;
  };

  const handleDropdownClick = () => {
    sendEvent(isFree
      ? 'freereview-actions-downloadpdf-dropdown'
      : 'review-actions-downloadpdf-btn');
    openMenu();
  };

  const handleDefaultPdfDownload = () => {
    if (!enablePdfDowload) {
      if (isFree) {
        sendEvent('freereview-actions-downloadpdf-btn');
      }
      openMenu();
    } else {
      if (sortedPdfTemplate[0]) {
        const { id, defaultTemplate } = sortedPdfTemplate[0];
        handleDownloadPdf({ id, defaultTemplate });
      } else {
        handleDownloadPdf({ defaultTemplate: true });
      }
    }
  };

  const sortedPdfTemplate = enablePdfDowload && pdfTemplates.reduce(
    (sortedPdfTemplate, pdfTemplate) => {
      if (pdfTemplate.default) {
        sortedPdfTemplate.unshift(pdfTemplate);
      } else {
        sortedPdfTemplate.push(pdfTemplate);
      }
      return sortedPdfTemplate;
    },
    []
  );

  const handleFreeTrial = () => {
    sendEvent(isFree
      ? 'freereview-actions-downloadpdf-register-btn'
      : 'review-actions-downloadpdf-upgradeplan-btn'
    );
  };

  return (
    <>
      <DownloadPdfDialog open={error} errorMessage={errorMessage} />
      <ButtonGroup
        disabled={busy}
        variant='outlined'
        size='small'
        className={classNameProp}
      >
        <Button
          className='nr-review-downloadpdf-button'
          size='small'
          onClick={handleDefaultPdfDownload}
        >
          <Translate content='get_the_pdf_report' />
        </Button>
        {!isDudaUser && (
          <Button
            className='nr-review-downloadpdf-dropdown'
            ref={(el) => {
              pdfBtn.current = el;
            }}
            onClick={handleDropdownClick}
          >
            {
              busy
                ? <Icon type='loader' color='grey' size='sm' />
                : <Icon size='sm' type='chevron-down' color='grey' />
            }
          </Button>
        )}
      </ButtonGroup>
      {!enablePdfDowload
        ? (
          <Popover
            anchorEl={pdfBtn.current}
            onClose={closeMenu}
            open={showMenu}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center'
            }}
          >
            <Translate
              component='div'
              content={isFree ? 'upgrade_unlock_feature' : 'pdf_download_blocked'}
              className={classes.upgrade}
            />
            <div className={classes.btnCtaContainer}>
              <Button
                variant='contained'
                color='success'
                onClick={handleFreeTrial}
                href={isFree ? `/${locale}/signup?plan=v2pro` : `/${locale}/user/plan?pdfBlckd=1`}
              >
                {
                  isFree
                    ? <Translate content='free_account_overview_cta_dynamic_trial' with={{ trial: settings.get('trialPeriod') }} />
                    : <Translate content='pdf_download_blocked_upgrade' />
                }
              </Button>
            </div>
          </Popover>
          )
        : (
          <Menu
            id='pdf-dropdown'
            anchorEl={pdfBtn.current}
            open={showMenu}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            PaperProps={{
              style: {
                maxHeight: ITEM_HEIGHT * MAX_ITEMS
              }
            }}
            getContentAnchorEl={null}
            onClose={closeMenu}
          >
            {sortedPdfTemplate.map(({ name, id, default: defaultTemplate }) => (
              <MenuItem
                key={id}
                onClick={() => handleDownloadPdf({ id, defaultTemplate })}
                className={classes.item}
              >
                <Typography noWrap>
                  {name}
                </Typography>
                {defaultTemplate && (
                  <Chip
                    variant='outlined'
                    size='small'
                    color='primary'
                    className={classes.defaultTemplateChip}
                    label={<Translate content='default' />}
                  />
                )}
                <IconButton
                  className={classes.editIcon}
                  onClick={(event) => handleEditTemplate(event, id, defaultTemplate)}
                >
                  <Icon size='sm' type='edit' />
                </IconButton>
              </MenuItem>
            ))}
            <MenuItem className={classes.newTemplateContainer}>
              <Button
                size='small'
                variant='outlined'
                onClick={() => sendEvent('review-actions-downloadpdf-newtemplate-link')}
                href={`/${locale}/pdf-editor#new`}
              >
                <Typography noWrap>
                  <Translate content='pdf_editor_new_template' />
                </Typography>
              </Button>
            </MenuItem>
          </Menu>
          )}
    </>
  );
};

export default DownloadPdfBtn;
